import { EFinancingProgram } from '@if/core/types'
import { QueryFunctionContext, useQuery } from '@tanstack/react-query'
import { getApiClient } from '../api-client'

export interface WarrantyPriceTierDto {
  maxVehiclePrice: number | null
  maxWarrantyAmount: number
}

export interface FinancingConfigDto {
  financingProgramId: EFinancingProgram
  longTerm: number
  largeLoanAmount: number
  minimumLoanAmount: number
  maximumLoanAmount: number
  effectiveRateThreshold: number
  maxLoanTerm: number
  minLoanTerm: number
  maxProductsTotalAmount?: number
  warrantyMaxByVehiclePriceTier?: WarrantyPriceTierDto[]
}

const FINANCING_CONFIG = 'financingConfig'
const fiveMinutes = 300000

const keysFactory = {
  financingConfig: (merchantId: string, financingProgram: EFinancingProgram) =>
    [{ scope: FINANCING_CONFIG, merchantId, financingProgram }] as const,
}

const createGetMerchantFinancingConfig = (merchantId: string, financingProgramId: EFinancingProgram) => {
  return async ({
    queryKey: [_],
  }: QueryFunctionContext<ReturnType<(typeof keysFactory)['financingConfig']>>) => {
    const apiClient = getApiClient()
    const response = await apiClient.get<FinancingConfigDto>(
      `Configs/Merchants/${merchantId}/FinancingConfigs/${financingProgramId}`,
    )
    return response.data
  }
}

export function useMerchantFinancingConfig(
  merchantId: string | null | undefined,
  financingProgramId: EFinancingProgram,
): [FinancingConfigDto | undefined, boolean, Error | null] {
  const { isFetching, data, error } = useQuery({
    queryKey: keysFactory.financingConfig(merchantId ?? '', financingProgramId),
    queryFn: createGetMerchantFinancingConfig(merchantId ?? '', financingProgramId),
    staleTime: fiveMinutes,
    gcTime: fiveMinutes,
    enabled: !!merchantId,
  })

  return [data, isFetching, error]
}
